import { EditorView } from "@codemirror/view";
import type { Extension } from "@codemirror/state";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags as t } from "@lezer/highlight";

// Light theme base styles
export const qlikThemeLight = EditorView.theme(
  {
    "&": {
      fontSize: "13px",
      fontFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    },
    ".cm-content": {
      padding: "8px 12px",
      minHeight: "160px",
      caretColor: "var(--color-foreground)",
    },
    ".cm-focused": {
      outline: "none",
    },
    ".cm-editor": {
      borderRadius: "8px",
      backgroundColor: "var(--color-background)",
    },
    ".cm-editor.cm-focused": {
      outline: "none",
    },
    ".cm-line": {
      color: "var(--color-foreground)",
    },
    // CodeMirror's default placeholder grey (#888) is below 4.5:1 on white.
    ".cm-placeholder": {
      color: "var(--color-dim)",
    },
    ".cm-cursor": {
      borderLeftColor: "var(--color-foreground)",
    },
    ".cm-selectionBackground": {
      backgroundColor: "color-mix(in srgb, var(--color-brand) 15%, transparent)",
    },
    "&.cm-focused .cm-selectionBackground": {
      backgroundColor: "color-mix(in srgb, var(--color-brand) 18%, transparent)",
    },
    ".cm-gutters": {
      display: "none",
    },
  },
  { dark: false },
);

// Dark theme overrides
export const qlikThemeDark = EditorView.theme(
  {
    ".cm-editor": {
      backgroundColor: "var(--color-background)",
    },
    ".cm-line": {
      color: "var(--color-foreground)",
    },
    ".cm-selectionBackground": {
      backgroundColor: "color-mix(in srgb, var(--color-brand) 20%, transparent)",
    },
    "&.cm-focused .cm-selectionBackground": {
      backgroundColor: "color-mix(in srgb, var(--color-brand) 25%, transparent)",
    },
  },
  { dark: true },
);

// Syntax highlight style — uses CSS variables so it works in both light/dark
export const qlikHighlightStyle = HighlightStyle.define([
  // Functions → blue (like keywords in IDEs)
  {
    tag: t.function(t.name),
    color: "hsl(217, 80%, 42%)",
    fontWeight: "500",
  },
  // Keywords (AND, OR, NOT, TOTAL, etc.) → purple
  {
    tag: t.keyword,
    color: "hsl(271, 70%, 44%)",
    fontWeight: "500",
  },
  // Strings (single-quoted values) → green
  {
    tag: t.string,
    color: "hsl(142, 65%, 26%)",
  },
  // Field names (double-quoted / bracket-quoted) → brand teal
  {
    tag: t.special(t.string),
    color: "var(--color-brand)",
  },
  // Numbers → readable warm tone
  {
    tag: t.number,
    color: "hsl(25, 90%, 36%)",
  },
  // Dollar-sign expansions $(var) → editorial accent
  {
    tag: t.special(t.variableName),
    color: "var(--color-editorial)",
    fontWeight: "500",
  },
  // Operators → default foreground, slightly dimmed
  {
    tag: t.operator,
    color: "var(--color-muted-foreground)",
  },
  // Comments → muted
  {
    tag: t.comment,
    color: "var(--color-muted-foreground)",
    fontStyle: "italic",
  },
  // Set analysis brackets {} <> → slightly highlighted
  {
    tag: t.bracket,
    color: "hsl(330, 70%, 40%)",
    fontWeight: "600",
  },
  // Plain identifiers/field names → foreground
  {
    tag: t.name,
    color: "var(--color-foreground)",
  },
]);

export function qlikThemeExtension(isDark: boolean): Extension {
  return [isDark ? qlikThemeDark : qlikThemeLight, syntaxHighlighting(qlikHighlightStyle)];
}
